function validAuthority(value) {
    return (
        value !== null &&
        typeof value === 'object' &&
        Number.isSafeInteger(value.generation) &&
        value.generation >= 0 &&
        ['enabled', 'paused', 'stopped'].includes(value.state) &&
        typeof value.owner_matches === 'boolean'
    );
}

function intentRetired(authority, intent, checkedPublisher) {
    const target = intent.generation ?? intent.expected_generation + 1;
    return (
        authority.generation > target ||
        (authority.state === 'stopped' && authority.generation >= target) ||
        (authority.generation === target &&
            intent.publisher_id !== undefined &&
            checkedPublisher === intent.publisher_id &&
            !authority.owner_matches)
    );
}

export function createDeliveryTrackingSession(dependencies) {
    const fetchImpl = dependencies.fetch ?? fetch;
    const makePublisher =
        dependencies.publisherIdFactory ?? (() => crypto.randomUUID());
    const onState = dependencies.onState ?? (() => {});
    let revision = 0;
    let verificationRevision = 0;
    let state = 'stopped';
    let optedIn = false;
    let verified = false;
    let publisherId = null;
    let generation = null;
    let pendingStart = null;
    let pendingStop = null;
    let acquisitionPromise = null;
    let lastAcceptedAt = null;
    let lastAcceptedSequence = 0;

    const snapshot = () => ({
        state,
        optedIn,
        publisherId,
        generation,
        lastAcceptedAt,
    });
    const emit = (next) => {
        state = next;
        onState(snapshot());
    };
    const request = async (url, method, body = null) => {
        const response = await fetchImpl(url, {
            method,
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': dependencies.csrfToken ?? '',
            },
            ...(body === null ? {} : { body: JSON.stringify(body) }),
        });
        if (!response.ok) {
            let code = null;
            try {
                code = (await response.json()).code;
            } catch {
                code = null;
            }
            const failure = new Error('Tracking session request unavailable.');
            failure.trackingState =
                code === 'mfa_required'
                    ? 'mfa_required'
                    : [401, 403, 419].includes(response.status)
                      ? 'session_required'
                      : response.status === 409
                        ? 'session_conflict'
                        : 'session_unavailable';
            throw failure;
        }
        if (method === 'DELETE' || url.endsWith('/cancel-start')) {
            return null;
        }
        const authority = await response.json();
        if (!validAuthority(authority)) {
            throw new Error('Tracking session authority unavailable.');
        }
        return authority;
    };
    const statusUrl = () =>
        `${dependencies.sessionUrl}?publisher_id=${encodeURIComponent(publisherId ?? '')}`;

    const confirmActivation = (authority) => {
        pendingStart = null;
        pendingStop = null;
        generation = authority.generation;
        lastAcceptedAt = null;
        lastAcceptedSequence = 0;
        optedIn = true;
        verified = true;
        emit(authority.state);
    };

    const acquire = async (takeover) => {
        const currentRevision = ++revision;
        const previousPublisher = publisherId;
        optedIn = false;
        verified = false;
        emit('starting');
        try {
            if (takeover && (pendingStart !== null || pendingStop !== null)) {
                emit('stopped_pending_ack');
                return false;
            }
            const retainedPublisher =
                pendingStart?.publisher_id ?? pendingStop?.body.publisher_id;
            publisherId = retainedPublisher ?? publisherId ?? makePublisher();
            const authority = await request(statusUrl(), 'GET');
            if (currentRevision !== revision) return false;
            generation =
                authority.state === 'stopped' ? null : authority.generation;
            if (
                pendingStop !== null &&
                intentRetired(authority, pendingStop.body, publisherId)
            ) {
                pendingStop = null;
            }
            if (pendingStop !== null) {
                emit('stopped_pending_ack');
                return false;
            }
            if (
                pendingStart !== null &&
                authority.state !== 'stopped' &&
                authority.owner_matches &&
                authority.generation === pendingStart.expected_generation + 1
            ) {
                confirmActivation(authority);
                return true;
            }
            if (
                pendingStart !== null &&
                intentRetired(authority, pendingStart, publisherId)
            ) {
                pendingStart = null;
            }
            if (
                !takeover &&
                authority.state !== 'stopped' &&
                !authority.owner_matches
            ) {
                generation = authority.generation;
                emit('owned_elsewhere');
                return false;
            }
            if (previousPublisher !== null && pendingStart === null) {
                publisherId = makePublisher();
            }
            const intent = {
                publisher_id: publisherId,
                expected_generation: authority.generation,
            };
            pendingStart = intent;
            const acquired = await request(
                `${dependencies.sessionUrl}${takeover ? '/takeover' : ''}`,
                'POST',
                intent,
            );
            if (currentRevision !== revision) return false;
            if (
                acquired.state !== 'enabled' ||
                !acquired.owner_matches ||
                acquired.generation !== authority.generation + 1
            ) {
                throw new Error('Tracking session acquisition unavailable.');
            }
            confirmActivation(acquired);
            return true;
        } catch (error) {
            if (currentRevision === revision) {
                emit(error.trackingState ?? 'session_unavailable');
            }
            return false;
        }
    };

    const stop = async () => {
        const currentRevision = ++revision;
        acquisitionPromise = null;
        const intent =
            pendingStop ??
            (pendingStart !== null
                ? {
                      url: `${dependencies.sessionUrl}/cancel-start`,
                      method: 'POST',
                      body: { ...pendingStart },
                  }
                : generation !== null
                  ? {
                        url: dependencies.stopUrl,
                        method: 'DELETE',
                        body: { generation },
                    }
                  : null);
        optedIn = false;
        verified = false;
        pendingStart = null;
        generation = null;
        pendingStop = intent;
        emit(intent === null ? 'stopped' : 'stopped_pending_ack');
        if (intent === null) return true;
        try {
            await request(intent.url, intent.method, intent.body);
            if (currentRevision === revision) {
                pendingStop = null;
                emit('stopped');
            }
            return true;
        } catch {
            return false;
        }
    };

    const verify = async () => {
        if (!optedIn || generation === null) return false;
        const currentRevision = revision;
        const currentVerification = ++verificationRevision;
        verified = false;
        try {
            const authority = await request(statusUrl(), 'GET');
            if (
                currentRevision !== revision ||
                currentVerification !== verificationRevision
            )
                return false;
            if (
                authority.state === 'stopped' ||
                !authority.owner_matches ||
                authority.generation !== generation
            ) {
                optedIn = false;
                generation =
                    authority.state === 'stopped' ? null : authority.generation;
                emit(
                    authority.state === 'stopped'
                        ? 'stopped'
                        : 'owned_elsewhere',
                );
                return false;
            }
            verified = true;
            emit(authority.state);
            return true;
        } catch (error) {
            if (
                currentRevision === revision &&
                currentVerification === verificationRevision
            ) {
                if (
                    error.trackingState !== undefined &&
                    error.trackingState !== 'session_unavailable'
                ) {
                    optedIn = false;
                }
                emit(error.trackingState ?? 'session_unavailable');
            }
            return false;
        }
    };

    return {
        start() {
            if (optedIn) return verify();
            if (acquisitionPromise !== null) return acquisitionPromise;
            const promise = acquire(false).finally(() => {
                if (acquisitionPromise === promise) acquisitionPromise = null;
            });
            acquisitionPromise = promise;
            return promise;
        },
        takeover: () => acquire(true),
        async observe() {
            if (
                optedIn ||
                pendingStart !== null ||
                pendingStop !== null ||
                acquisitionPromise !== null
            )
                return false;
            const currentRevision = ++revision;
            verified = false;
            try {
                const authority = await request(
                    publisherId === null
                        ? dependencies.sessionUrl
                        : statusUrl(),
                    'GET',
                );
                if (currentRevision !== revision) return false;
                generation =
                    authority.state === 'stopped' ? null : authority.generation;
                emit(generation === null ? 'stopped' : 'owned_elsewhere');
                return true;
            } catch (error) {
                if (currentRevision === revision)
                    emit(error.trackingState ?? 'session_unavailable');
                return false;
            }
        },
        stop,
        verify,
        snapshot,
        publicationIdentity: () =>
            optedIn && verified && generation !== null
                ? { publisher_id: publisherId, generation }
                : null,
        acceptPublication(acknowledgment, identity) {
            if (
                !optedIn ||
                !verified ||
                identity?.generation !== generation ||
                identity?.publisher_id !== publisherId ||
                acknowledgment?.accepted !== true ||
                !Number.isSafeInteger(acknowledgment.sequence) ||
                acknowledgment.sequence <= lastAcceptedSequence ||
                typeof acknowledgment.received_at !== 'string' ||
                !Number.isFinite(Date.parse(acknowledgment.received_at)) ||
                (acknowledgment.generation !== undefined &&
                    acknowledgment.generation !== generation)
            ) {
                return false;
            }
            lastAcceptedSequence = acknowledgment.sequence;
            lastAcceptedAt = acknowledgment.received_at;
            onState(snapshot());
            return true;
        },
    };
}
